import { type MessageKey, useT } from '@/shared/lib/i18n';
import { AdaptiveDialog } from './AdaptiveDialog';
import { BrandMark } from './BrandMark';
import { Button } from './Button';
import { HandKicker } from './HandKicker';
import { PhaseTag } from './PhaseTag';
import { Pin } from './Pin';

export type RulesSheetProps = {
  open: boolean;
  onClose: () => void;
};

type Step = {
  num: string;
  titleKey: MessageKey;
  bodyKey: MessageKey;
};

const STEPS: readonly Step[] = [
  { num: '01', titleKey: 'rules.step1.title', bodyKey: 'rules.step1.body' },
  { num: '02', titleKey: 'rules.step2.title', bodyKey: 'rules.step2.body' },
  { num: '03', titleKey: 'rules.step3.title', bodyKey: 'rules.step3.body' },
];

/**
 * "How to play" dialog — same dossier chrome as the mugshot editor and
 * lobby host-settings sheet (bare AdaptiveDialog + paper-dossier
 * surface, two corner pins, PhaseTag + BrandMark header). Numbered
 * steps stack vertically with ink dividers, then an orange "Got it" CTA
 * footer.
 */
export const RulesSheet = ({ open, onClose }: RulesSheetProps) => {
  const t = useT();

  return (
    <AdaptiveDialog
      open={open}
      onClose={onClose}
      ariaLabel={t('rules.title')}
      closeLabel={t('settings.close')}
      surface="bare"
      hideHandle
      desktopMaxWidth={640}
    >
      <div className="paper-dossier relative flex flex-col gap-5 px-7 py-6 text-ink">
        <Pin left={28} top={-6} />
        <Pin left={'calc(100% - 28px)'} top={-6} />

        <header className="flex flex-col gap-1">
          <PhaseTag size={11} color="rgba(0,0,0,.5)">
            {t('rules.eyebrow')}
          </PhaseTag>
          <BrandMark size={'clamp(36px, 4vw, 46px)'} className="mt-1">
            {t('rules.title')}
          </BrandMark>
          <HandKicker size={20} color="var(--color-pencil)" rot={0} className="mt-2">
            — {t('rules.sub')}
          </HandKicker>
        </header>

        <ol className="flex flex-col">
          {STEPS.map((step, idx) => (
            <li
              key={step.num}
              className={['flex gap-4 py-4', idx < STEPS.length - 1 ? 'border-ink/35 border-b' : '']
                .filter(Boolean)
                .join(' ')}
            >
              <span
                aria-hidden
                className="brand-mark shrink-0 leading-none"
                style={{ fontSize: 'clamp(36px, 6vw, 48px)', minWidth: '1.6em' }}
              >
                {step.num}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-display font-extrabold text-[16px] text-ink leading-tight">
                  {t(step.titleKey)}
                </div>
                <p className="type mt-1.5 text-[13px] text-ink/85 leading-[1.55]">
                  {t(step.bodyKey)}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <footer className="flex justify-end pt-1">
          <Button variant="orange" fullWidth={false} onClick={onClose}>
            {t('rules.got_it')} →
          </Button>
        </footer>
      </div>
    </AdaptiveDialog>
  );
};
